import React, { useMemo, useCallback, ReactElement } from 'react';
import { BDSProvider, DataTable, Grid, Button, Typography, Box } from '@barracuda-internal/bds-core-v2';
import Gravatar from 'react-gravatar';
import useAuth from 'src/hooks/useAuth';
import { UserDetail, User, UserAssignment } from 'src/types/dataTypes';
import { roleMap } from './roleMap';

interface UserRow {
	original: {
		user?: User;
		assignments?: UserAssignment[];
	} & UserDetail;
}

interface UserTableProps {
	isLoading: boolean;
	data: UserDetail[];
	page: number;
	pageSize: number;
	totalRecords: number;
	onEditUser: (user: UserDetail) => void;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
}

function UserTable({
	data,
	isLoading,
	page,
	pageSize,
	totalRecords,
	onEditUser,
	onPageChange,
	onPageSizeChange,
}: UserTableProps): ReactElement {
	const { auth } = useAuth();

	const columns = useMemo(
		() => [
			{
				accessorKey: 'user.name',
				header: 'User',
				enableColumnActions: false,
				enableSorting: false,
				Cell: ({ row }: { row: UserRow }) => {
					const user = row.original.user;
					if (!user?.email || !user?.name) {
						return <Typography variant="body1">Unknown</Typography>;
					}
					return (
						<Box sx={{ display: 'flex', alignItems: 'center' }}>
							<Gravatar email={user.email} size={40} style={{ borderRadius: '50%' }} />
							<Typography variant="body1" sx={{ ml: 1.5 }}>
								{user.name}
							</Typography>
						</Box>
					);
				},
			},
			{
				accessorKey: 'user.email',
				header: 'Email',
				enableColumnActions: false,
				enableSorting: false,
			},
			{
				accessorKey: 'assignments',
				header: 'Roles',
				enableColumnActions: false,
				enableSorting: false,
				Cell: ({ row }: { row: UserRow }) => {
					const accountRoles = row.original.assignments?.filter((a) =>
						a.domain.startsWith(
							`/accounts/${auth.accountDetails.account.id}`,
						),
					);
					return (
						<Box>
							{accountRoles?.map((role) => (
								<div key={role.role}>{roleMap[role.role]}</div>
							))}
						</Box>
					);
				},
			},
			{
				accessorKey: 'actions',
				header: 'Actions',
				enableColumnActions: false,
				enableSorting: false,
				Cell: ({ row }: { row: UserRow }) => (
					<Button
						onClick={() => onEditUser(row.original)}
						variant="contained"
						color="primary"
						sx={{ ml: 1 }}
					>
						Edit
					</Button>
				),
			},
		],
		[auth.accountDetails.account.id, onEditUser],
	);

	const handlePaginationChange = useCallback(
		(event, value) => {
			onPageChange(value);
		},
		[onPageChange],
	);

	const handleOnClickItemsPerPageOption = useCallback(
		(itemsPerPageValue: number) => {
			onPageSizeChange(itemsPerPageValue);
		},
		[onPageSizeChange],
	);

	return (
		<BDSProvider>
			<Grid container sx={{ width: '100%', minWidth: '900px', overflowX: 'auto' }}>
				<DataTable
					layoutMode="semantic"
					columns={columns}
					data={data || []}
					state={{
						isLoading,
					}}
					enableTopToolbar={false}
					enableFilters={false}
					enableGrouping={false}
					enableColumnDragging={false}
					enableColumnFilterModes={false}
					enablePagination={false}
					enableStickyHeader={true}
					enableGlobalFilter={false}
					enableColumnActions={false}
					showSearchFields={false}
					muiTableBodyCellProps={{ sx: { padding: '12px' } }}
					muiTableHeadCellProps={{ sx: { padding: '12px !important' } }}
					muiTableContainerProps={{
						sx: {
							overflowY: 'auto',
							height: 'calc(100vh - 350px)',
							maxHeight: 'none',
						},
					}}
					paginationProps={{
						page: page,
						totalItems: totalRecords,
						itemsPerPage: pageSize,
						itemsPerPageOptions: [10, 25, 50, 100],
						itemsPerPageDescription: 'items per page',
						onChange: handlePaginationChange,
						onClickItemsPerPageOption: handleOnClickItemsPerPageOption,
					}}
					enableBottomToolbar={totalRecords > 0}
				/>
			</Grid>
		</BDSProvider>
	);
}

export default UserTable;
